import { useEffect, useRef, useState } from 'react';
import { koppelVerkoopsorder } from '@/api/projects';
import { searchProjects } from '@/api/planning';
import type { ProjectSearchResult } from '@/api/planning';
import { ApiError } from '@/api/client';

export interface KoppelVerkoopsorderModalProps {
  projectId: number;
  projectName: string;
  reinvoicedSaleOrder?: [number, string] | false;
  onClose: () => void;
  onSuccess: (targetProjectId: number) => void;
}

type Screen = 'search' | 'confirm';

function mapErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.status === 409) return 'Dit project is al aan een verkoopsorder gekoppeld.';
    if (err.status === 422) return 'Doelproject is geen SO-project. Kies een project met een gekoppelde verkoopsorder.';
  }
  return err instanceof Error ? err.message : 'Koppeling mislukt. Probeer opnieuw.';
}

export function KoppelVerkoopsorderModal({
  projectId,
  projectName,
  reinvoicedSaleOrder,
  onClose,
  onSuccess,
}: KoppelVerkoopsorderModalProps) {
  const initialQuery =
    Array.isArray(reinvoicedSaleOrder) ? reinvoicedSaleOrder[1] : '';

  const [screen, setScreen] = useState<Screen>('search');
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<ProjectSearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<ProjectSearchResult | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  useEffect(() => {
    if (initialQuery.trim().length >= 2) {
      performSearch(initialQuery);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function performSearch(q: string) {
    setSearching(true);
    setError(null);
    searchProjects(q)
      .then((res) => {
        setResults(res.results);
      })
      .catch(() => {
        setResults([]);
      })
      .finally(() => {
        setSearching(false);
      });
  }

  function handleQueryChange(e: React.ChangeEvent<HTMLInputElement>) {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (val.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      performSearch(val.trim());
    }, 300);
  }

  function handleSelectResult(result: ProjectSearchResult) {
    setSelected(result);
    setScreen('confirm');
    setError(null);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!selected || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await koppelVerkoopsorder(projectId, selected.id);
      onSuccess(selected.id);
    } catch (err) {
      setError(mapErrorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="koppel-so-title"
    >
      <div className="w-full max-w-md rounded-xl bg-white shadow-xl flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <h3 id="koppel-so-title" className="text-base font-extrabold text-td-navy">
            Koppel aan verkoopsorder
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Sluiten"
            className="w-8 h-8 rounded-full flex items-center justify-center text-td-muted hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {screen === 'search' && (
          <>
            <div className="p-5 space-y-3">
              <label htmlFor="koppel-so-search" className="block text-sm font-semibold text-td-navy">
                Zoek doelproject (naam of S-nummer)
              </label>
              <div className="relative">
                <input
                  id="koppel-so-search"
                  ref={inputRef}
                  type="text"
                  value={query}
                  onChange={handleQueryChange}
                  placeholder="bijv. S26318681 of Projectnaam"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-td-navy focus:outline-none focus:ring-2 focus:ring-td-blue"
                />
                {searching && (
                  <span
                    className="absolute right-3 top-1/2 -translate-y-1/2 inline-block w-4 h-4 border-2 border-td-blue/30 border-t-td-blue rounded-full animate-spin"
                    role="status"
                    aria-label="Zoeken..."
                  />
                )}
              </div>

              {results.length > 0 && (
                <ul
                  className="max-h-60 overflow-y-auto divide-y divide-gray-100 rounded-lg border border-gray-200"
                  role="listbox"
                  aria-label="Zoekresultaten"
                >
                  {results.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={false}
                        onClick={() => handleSelectResult(r)}
                        className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-td-navy">{r.projectName}</span>
                        {r.sale_order_name && (
                          <span className="ml-2 text-xs text-td-muted">{r.sale_order_name}</span>
                        )}
                        <div className="text-xs text-td-muted mt-0.5">{r.address}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <p className="text-sm text-td-muted">Geen projecten gevonden.</p>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="flex justify-end p-5 pt-0">
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-td-navy hover:bg-gray-100 transition-colors"
              >
                Annuleren
              </button>
            </div>
          </>
        )}

        {screen === 'confirm' && selected && (
          <form onSubmit={handleSubmit}>
            <div className="p-5 space-y-4">
              <p className="text-sm text-td-navy">
                Alle taken worden verplaatst naar{' '}
                <strong className="font-semibold">«{selected.projectName}»</strong>, het
                tijdelijke project{' '}
                <strong className="font-semibold">«{projectName}»</strong> wordt gearchiveerd.
              </p>

              {selected.sale_order_name && (
                <p className="text-xs text-td-muted">
                  Verkoopsorder: {selected.sale_order_name}
                </p>
              )}

              {error && <p className="text-sm text-red-600">{error}</p>}
            </div>

            <div className="flex justify-end gap-2 p-5 pt-0">
              <button
                type="button"
                onClick={() => { setScreen('search'); setError(null); }}
                disabled={submitting}
                className="rounded-lg px-4 py-2 text-sm font-semibold text-td-navy hover:bg-gray-100 transition-colors disabled:opacity-50"
              >
                Terug
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="rounded-lg bg-td-blue px-4 py-2 text-sm font-semibold text-white hover:bg-td-blue/90 transition-colors disabled:opacity-50 inline-flex items-center gap-2"
              >
                {submitting && (
                  <span
                    className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                    role="status"
                    aria-hidden="true"
                  />
                )}
                Koppelen
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
